import {
  MATCH_THRESHOLD_REVIEW,
  speakerMatchScore,
} from '@src/utility/speakerFuzzyMatch';

import { LogManager } from './Logger';

import type { SpeakerData } from '@src/services/SpeakerManagementService';
import type { FrameAnalysisResult } from '@src/utility/frameExtraction';

const logger = LogManager.getLogger('speakerIdentification');

interface SpeakerSegment {
  speaker: string;
  content: string;
}

interface TurnSegment {
  speaker: string;
  content: string;
  charLength: number;
  selfIntroIndex: number | null;
  thirdPersonIntroIndex: number | null;
  index: number;
}

function getSelfIntroRegex() {
  return /\b(i'm|i am|my name is)\b/i;
}

function getThirdPersonIntroRegex() {
  return /(joined by|with us|today we have|here with|i'm joined by|i am joined by|to introduce)/i;
}

function findFirstIndex(text: string, regex: RegExp): number | null {
  const re = new RegExp(regex.source, regex.flags);
  const m = re.exec(text);
  return m ? m.index : null;
}

function findLastIndex(text: string, regex: RegExp): number | null {
  // Make sure the regex is global so we can iterate through all matches
  const re = new RegExp(
    regex.source,
    regex.flags.includes('g') ? regex.flags : regex.flags + 'g',
  );

  let lastIndex: number | null = null;
  let match: RegExpExecArray | null;

  while ((match = re.exec(text)) !== null) {
    lastIndex = match.index;
  }

  return lastIndex;
}

function windowSpeakerSegment(
  text: string,
  center: number,
  leftPad: number,
  rightPad: number,
): string {
  const start = Math.max(0, center - leftPad);
  const end = Math.min(text.length, center + rightPad);
  let headerPrefix = '';
  let prefix = '';
  if (start > 0) {
    const speakerHeaderMatch = text.match(/^\s*(Speaker\s+\d+:)/i);
    if (speakerHeaderMatch) {
      headerPrefix = `${speakerHeaderMatch[1]} `;
    } else {
      const colonIdx = text.indexOf(':');
      if (colonIdx > -1 && colonIdx < 50) {
        headerPrefix = `${text.slice(0, colonIdx + 1)} `;
      }
    }
    prefix = `${headerPrefix}…`;
  }
  const suffix = end < text.length ? '…' : '';
  return `${prefix}${text.slice(start, end)}${suffix}`;
}

/**
 * Build overlapping transcript chunks at speaker boundaries.
 * Extraction of the inline logic from identifySpeakers for clarity & reuse.
 * Strategy:
 *  - Grow a chunk until target char size OR speaker count threshold reached.
 *  - Avoid emitting tiny (< minNonFinalChunkChars) non-final chunks to reduce low-signal passes.
 *  - Overlap last N speaker segments to preserve identity continuity across chunk boundaries.
 */
export function buildOverlappingChunks(
  speakerSegments: {
    speaker: string;
    content: string;
    startIndex: number;
    endIndex: number;
  }[],
  options: {
    targetChunkSize: number;
    maxChunks: number;
    speakersPerChunk?: number;
    overlapSpeakers?: number;
    minChunkChars?: number; // absolute minimum to keep (after trim)
    minNonFinalChunkChars?: number; // threshold to suppress very small non-final chunk
    createThirdPersonSupplementalChunks?: boolean; // add extra chunks to cover third-person intros
    supplementalThirdPersonMax?: number; // limit the number of supplemental chunks
    capMultiplier?: number;
  },
): string[] {
  const {
    targetChunkSize,
    maxChunks,
    speakersPerChunk = 10,
    overlapSpeakers = 2,
    minChunkChars = 100,
    minNonFinalChunkChars = 500,
    createThirdPersonSupplementalChunks = true,
    supplementalThirdPersonMax = 3,
    capMultiplier = 1.5,
  } = options;
  const thirdRe = getThirdPersonIntroRegex();

  const chunks: string[] = [];
  const chunkRanges: Array<{ start: number; end: number }> = [];
  let currentChunkStartIdx = 0;
  // Hard cap to prevent any single chunk from becoming overwhelmingly large
  const capLimit = Math.floor(targetChunkSize * capMultiplier);

  // Pre-scan: detect third-person intro indices
  const thirdPersonIntroIndexes: number[] = [];
  for (let i = 0; i < speakerSegments.length - 1; i++) {
    if (findFirstIndex(speakerSegments[i].content, thirdRe) !== null) {
      thirdPersonIntroIndexes.push(i);
    }
  }

  while (
    currentChunkStartIdx < speakerSegments.length &&
    chunks.length < maxChunks
  ) {
    let currentChunkEndIdx = currentChunkStartIdx;
    let currentChunkSize = 0;

    // Grow chunk
    while (currentChunkEndIdx < speakerSegments.length) {
      currentChunkSize += speakerSegments[currentChunkEndIdx].content.length;
      currentChunkEndIdx++;

      // If we exceeded the hard cap, stop growing this chunk now.
      // We'll truncate the last segment's content when constructing chunk text below.
      if (currentChunkSize >= capLimit) {
        logger.warn(
          `Chunk reached cap limit (${capLimit} chars). Will truncate last segment content to fit.`,
        );
        break;
      }

      const speakersInChunk = currentChunkEndIdx - currentChunkStartIdx;
      const shouldEndChunk =
        currentChunkSize >= targetChunkSize ||
        speakersInChunk >= speakersPerChunk ||
        currentChunkEndIdx === speakerSegments.length;

      if (shouldEndChunk) {
        // Prevent emitting very small non-final chunks
        if (
          currentChunkSize > minNonFinalChunkChars ||
          currentChunkEndIdx === speakerSegments.length
        ) {
          break;
        }
      }
    }

    const chunkSegments = speakerSegments.slice(
      currentChunkStartIdx,
      currentChunkEndIdx,
    );
    // Build chunk text; if we exceeded capLimit, only truncate the last segment
    let chunkText = buildCapAwareChunkText(chunkSegments, currentChunkSize);
    if (chunkText.trim().length > minChunkChars) {
      chunks.push(chunkText);
      // Track range for coverage checks (end is exclusive)
      chunkRanges.push({
        start: currentChunkStartIdx,
        end: currentChunkEndIdx,
      });
      logger.info(
        `Chunk ${chunks.length}: ${chunkSegments.length} speakers, ${chunkText.length} chars`,
      );
    }

    if (currentChunkEndIdx < speakerSegments.length) {
      currentChunkStartIdx = Math.max(
        currentChunkStartIdx + 1,
        currentChunkEndIdx - overlapSpeakers,
      );
    } else {
      break;
    }
  }

  // After main chunking, opportunistically add supplemental chunks for uncovered third-person pairs
  addExtraChunksWithThirdPersonIntros();

  if (chunks.length >= maxChunks) {
    logger.warn(
      `Reached maximum chunk limit (${maxChunks}). Transcript may be exceptionally long.`,
    );
  }

  logger.info(`Created ${chunks.length} overlapping chunks for analysis`);
  return chunks;

  function addExtraChunksWithThirdPersonIntros() {
    if (
      createThirdPersonSupplementalChunks &&
      thirdPersonIntroIndexes.length > 0
    ) {
      let added = 0;
      for (const introIdx of thirdPersonIntroIndexes) {
        if (added >= supplementalThirdPersonMax) break;
        const isAlreadyCovered = chunkRanges.some(
          r => r.start <= introIdx && introIdx + 1 < r.end,
        );
        if (isAlreadyCovered) continue;
        const text = buildChunkBasedOnThirdPersonIntro(introIdx);
        if (text.trim().length > minChunkChars) {
          chunks.push(text);
          added += 1;
        }
      }
      if (added > 0) {
        logger.info(`Supplemental third-person chunks added: ${added}`);
      }
    }
  }

  function buildChunkBasedOnThirdPersonIntro(introSegmentIdx: number): string {
    const mentionSeg = speakerSegments[introSegmentIdx];
    const nextSeg = speakerSegments[introSegmentIdx + 1];
    // we want the closest intro mention to the end
    const center = findLastIndex(mentionSeg.content, thirdRe);
    if (center === null) {
      return '';
    }
    const introContext = windowSpeakerSegment(
      mentionSeg.content,
      center,
      1000,
      2000,
    );
    const introResponse = nextSeg.content.slice(0, 4000);
    let text = `${introContext}${introResponse}`;
    if (text.length > capLimit) {
      text = text.slice(0, Math.max(0, capLimit - 1)) + '…';
    }
    return text;
  }

  function buildCapAwareChunkText(
    chunkSegments: {
      speaker: string;
      content: string;
      startIndex: number;
      endIndex: number;
    }[],
    currentChunkSize: number,
  ): string {
    let chunkText = '';
    if (chunkSegments.length > 0) {
      const lastSeg = chunkSegments[chunkSegments.length - 1];
      // Sometimes the last segment makes the chunk grow too much, exceeding the cap limit.
      if (currentChunkSize > capLimit) {
        const prevText = chunkSegments
          .slice(0, -1)
          .map(seg => seg.content)
          .join('');
        const space = capLimit - prevText.length; // space available for last segment
        if (space <= 0) {
          // No space left for the last segment; if prevText already exceeds cap, truncate it to cap
          if (prevText.length >= capLimit) {
            chunkText = prevText.slice(0, Math.max(0, capLimit - 1)) + '…';
          } else {
            // PrevText fits; keep it and indicate truncation
            chunkText = prevText + '…';
          }
        } else {
          // Take as much as fits; if truncated, append an ellipsis
          const allowed = Math.min(space, lastSeg.content.length);
          const tail = lastSeg.content.slice(0, allowed);
          const truncated = allowed < lastSeg.content.length;
          chunkText = prevText + tail + (truncated ? '…' : '');
        }
      } else {
        // Within cap, include full contents
        chunkText = chunkSegments.map(seg => seg.content).join('');
      }
    }
    return chunkText;
  }
}

/**
 * Parse a full transcript into speaker-labeled segments.
 * Returns empty array if formatting not detected (caller can fallback to single-pass path).
 */
export function extractSpeakerSegments(fullTranscript: string): {
  speaker: string;
  content: string;
  startIndex: number;
  endIndex: number;
}[] {
  const speakerPattern = /^([^:]+):\s*/gm;
  const segments: {
    speaker: string;
    content: string;
    startIndex: number;
    endIndex: number;
  }[] = [];
  let lastIndex = 0;
  let currentSpeaker = '';
  let match: RegExpExecArray | null;

  const hasSpeakerFormat = speakerPattern.test(fullTranscript);
  speakerPattern.lastIndex = 0;
  if (!hasSpeakerFormat) {
    logger.info(
      'Transcript does not have speaker formatting, using single-pass approach',
    );
    return [];
  }

  while ((match = speakerPattern.exec(fullTranscript)) !== null) {
    const potentialSpeaker = match[1].trim();
    const isSpeaker =
      potentialSpeaker.includes('Speaker') ||
      potentialSpeaker.includes('spk_') ||
      (potentialSpeaker.length < 50 && !potentialSpeaker.includes('\n'));
    if (isSpeaker) {
      if (currentSpeaker) {
        const segmentContent = fullTranscript.substring(lastIndex, match.index);
        if (segmentContent.trim()) {
          segments.push({
            speaker: currentSpeaker,
            content: segmentContent,
            startIndex: lastIndex,
            endIndex: match.index,
          });
        }
      }
      currentSpeaker = potentialSpeaker;
      lastIndex = match.index;
    }
  }

  if (currentSpeaker && lastIndex < fullTranscript.length) {
    const segmentContent = fullTranscript.substring(lastIndex);
    if (segmentContent.trim()) {
      segments.push({
        speaker: currentSpeaker,
        content: segmentContent,
        startIndex: lastIndex,
        endIndex: fullTranscript.length,
      });
    }
  }

  if (segments.length === 0) {
    logger.info('No speaker segments found, using single-pass approach');
    return [];
  }

  logger.info(`Found ${segments.length} speaker segments`);
  return segments;
}

export function buildRosterTextForAnchors(
  speakerSegments: SpeakerSegment[],
  fullTranscript: string,
): string {
  const selfIntroRegex = getSelfIntroRegex();
  const thirdPersonIntroRegex = getThirdPersonIntroRegex();

  const turns: TurnSegment[] = speakerSegments.map((seg, idx) => {
    const text = seg.content;
    return {
      speaker: seg.speaker,
      content: text,
      charLength: text.length,
      selfIntroIndex: findFirstIndex(text, selfIntroRegex),
      thirdPersonIntroIndex: findFirstIndex(text, thirdPersonIntroRegex),
      index: idx,
    };
  });
  const TARGET_ROSTER_CHARS = 2000;
  const seenSpeakers = new Set<string>();
  const roster: TurnSegment[] = [];
  const priorityTurns = [
    ...turns.filter(t => t.selfIntroIndex !== null),
    ...turns.filter(
      t => t.selfIntroIndex === null && t.thirdPersonIntroIndex !== null,
    ),
    ...turns,
  ];

  for (const t of priorityTurns) {
    const isOverLimit =
      roster.reduce((sum, x) => sum + x.charLength, 0) > TARGET_ROSTER_CHARS;
    if (isOverLimit) break;
    let effectiveSpeaker = t.speaker;
    let snippet: string;
    if (t.selfIntroIndex !== null) {
      // Center around the self-introduction phrase
      snippet = windowSpeakerSegment(t.content, t.selfIntroIndex, 120, 260);
    } else if (t.thirdPersonIntroIndex !== null) {
      // Include context around the third-person mention and the next speaker's opening
      const hasNextTurn = t.index + 1 < turns.length;
      if (!hasNextTurn) {
        continue;
      }
      const nextTurn = turns[t.index + 1];
      const introContext = windowSpeakerSegment(
        t.content,
        t.thirdPersonIntroIndex,
        100,
        140,
      );
      const nextStart = nextTurn.content.slice(0, 140);
      snippet = `${introContext}${nextStart}`;
      effectiveSpeaker = nextTurn.speaker;
    } else {
      // Fallback to the beginning of the turn as before
      snippet =
        t.content.length > 400 ? t.content.slice(0, 380) + '…' : t.content;
    }
    snippet = snippet.trim();

    // Avoid duplicate speakers (dedupe by effective speaker)
    if (seenSpeakers.has(effectiveSpeaker)) continue;
    seenSpeakers.add(effectiveSpeaker);
    roster.push({
      ...t,
      speaker: effectiveSpeaker,
      content: snippet,
      charLength: snippet.length,
    });
  }
  let rosterText = roster.map(r => r.content).join('\n\n');
  if (rosterText.length < 500) {
    rosterText = fullTranscript.slice(0, 2000);
  }
  return rosterText;
}

// ============================================================================
// IDENTIFICATION RESULTS
//
// The helpers above prepare what we send to the model. These judge what comes
// back, and combine the answers from successive passes: the transcript first,
// then progressively denser sweeps of video frames looking for a lower third.
//
// Companion to `speakerFuzzyMatch`, which compares an identification against
// the speakers already in DatoCMS. Nothing here touches IO or React.
// ============================================================================

/** One speaker as an identification pass named them, keyed by transcript label. */
export interface IdentifiedSpeaker {
  speakerLabel: string;
  name: string;
  title: string;
  /**
   * The name parts, when the model gave them (PLAT-12086). A name a person
   * typed has none: the plugin never splits a name itself (DE10).
   */
  firstName?: string;
  lastName?: string;
  /**
   * The whole speaker as a person described them in the speaker modal from
   * the review (PLAT-12257): facts, lines, bio, voice, gender, headshots.
   * Apply creates exactly this; `name` and `title` are only what the review
   * shows and matches on. An identification has none, and since the model's
   * answer is spread in as it came, the key is one it cannot hold.
   */
  draft?: SpeakerData;
}

/**
 * The name parts of an identification as the model answered them, read from
 * either the schema's `first_name` / `last_name` or camel-cased keys, trimmed.
 * Undefined when neither is a non-empty string.
 */
export function identifiedNameParts(
  raw: Record<string, unknown>,
): { firstName?: string; lastName?: string } | undefined {
  const text = (v: unknown) =>
    typeof v === 'string' && v.trim() ? v.trim() : undefined;
  const firstName = text(raw.first_name) ?? text(raw.firstName);
  const lastName = text(raw.last_name) ?? text(raw.lastName);
  if (!firstName && !lastName) return undefined;
  return {
    ...(firstName ? { firstName } : {}),
    ...(lastName ? { lastName } : {}),
  };
}

/**
 * Split an ID Speaker title into the speaker's facts (PLAT-11999 DS1,
 * PLAT-12180).
 *
 * The model answers "Company | Position" ("Acme Corp | CEO"), and the whole
 * identification pipeline — chunk voting, role-word checks, fuzzy matching,
 * the review rows — runs on that combined string. Only when a NEW speaker is
 * saved is it split, on the FIRST `|`: the company to `organization`, the
 * position to `role`, and no Title, so the speaker's one line is derived in
 * the showcase's Speaker Format (PLAT-12176). A title without `|` (a bare
 * role, or one the person typed) is kept as the typed Title, with no facts.
 */
export function splitIdentifiedTitle(title?: string | null): {
  title?: string;
  role?: string;
  organization?: string;
} {
  const trimmed = title?.trim() ?? '';
  const bar = trimmed.indexOf('|');
  if (bar === -1) return trimmed ? { title: trimmed } : {};
  const organization = trimmed.slice(0, bar).trim();
  const role = trimmed.slice(bar + 1).trim();
  return {
    ...(role ? { role } : {}),
    ...(organization ? { organization } : {}),
  };
}

export type IdentificationStrength = 'complete' | 'partial';

/**
 * Words that describe a role rather than name a person. The transcript pass
 * sometimes answers "Vanguard executive" or "Company representative" when no
 * one is named on the record; those are labels, not identities. Several are
 * also real surnames (Guest, Head, Partner), which is why one alone never
 * makes a name a role description — see isRoleDescription.
 */
const ROLE_WORDS = new Set([
  'executive',
  'representative',
  'rep',
  'advisor',
  'adviser',
  'spokesperson',
  'spokesman',
  'spokeswoman',
  'host',
  'interviewer',
  'interviewee',
  'moderator',
  'narrator',
  'guest',
  'speaker',
  'employee',
  'leader',
  'ceo',
  'cfo',
  'cto',
  'coo',
  'president',
  'director',
  'manager',
  'officer',
  'founder',
  'analyst',
  'unknown',
  'unidentified',
  'unnamed',
  'member',
  'participant',
  'panelist',
  'presenter',
  'reporter',
  'anchor',
  'chair',
  'chairman',
  'chairwoman',
  'partner',
  'head',
  'chief',
]);

/**
 * Words that qualify a role without naming anyone: "Unidentified man",
 * "Audience member", "Company representative".
 */
const FILLER_WORDS = new Set([
  'a',
  'an',
  'the',
  'of',
  'company',
  'audience',
  'man',
  'woman',
  'male',
  'female',
  'person',
]);

const words = (text: string) => text.toLowerCase().match(/[\p{L}']+/gu) ?? [];

/**
 * Whether the "name" is really a role or organisation, so it named no one and
 * any name a lower third reads may replace it.
 *
 * A role word only counts when nothing else in the name could be a person:
 * every word is a role, filler or title word ("Vanguard executive" for a
 * Vanguard employee), the role word is written lowercase ("Acme spokesperson"),
 * or it qualifies something after it ("Vanguard Financial Advisor Services").
 * So "Christopher Guest" and "Michael Dell" (title "Dell Technologies | CEO")
 * stay names. A lone word repeating the title ("Vanguard") is the organisation.
 */
export function isRoleDescription(s: {
  name: string;
  title?: string;
}): boolean {
  const titleWords = new Set(words(s.title ?? ''));
  const original = s.name.match(/[\p{L}']+/gu) ?? [];
  const lower = original.map(w => w.toLowerCase());
  if (lower.length === 1 && titleWords.has(lower[0])) return true;
  const roleAt = lower
    .map((w, i) => (ROLE_WORDS.has(w) ? i : -1))
    .filter(i => i >= 0);
  if (roleAt.length === 0) return false;
  return (
    lower.every(
      w => ROLE_WORDS.has(w) || FILLER_WORDS.has(w) || titleWords.has(w),
    ) || roleAt.some(i => original[i] === lower[i] || i < lower.length - 1)
  );
}

/**
 * Whether the name only repeats the speaker's own title — "Charles Schwab"
 * for "Charles Schwab | Founder". Usually a founder whose company carries
 * their name, but it may be the company itself, so it is worth a look at the
 * frames; unlike a role description it still names someone, and keeps its say
 * over a lower third that contradicts it.
 */
function echoesTitle(s: { name: string; title?: string }): boolean {
  const titleWords = new Set(words(s.title ?? ''));
  const nameWords = words(s.name).filter(w => !FILLER_WORDS.has(w));
  return nameWords.length > 0 && nameWords.every(w => titleWords.has(w));
}

/**
 * How much of a person an identification actually pins down.
 *
 * Measured on the name: a lone token cannot tell two people who share it
 * apart, while a first and last name can. A name that is really a role
 * description ("Vanguard executive"), or only repeats the title, is partial
 * however many words it has.
 * A title is not required — the model infers titles from speech rather than
 * reading them off the video, so its presence says only that something was
 * written, and speakers who genuinely have no title would otherwise never
 * count as identified. It is read only to spot a role or a repeat of it.
 */
export function identificationStrength(s: {
  name: string;
  title?: string;
}): IdentificationStrength {
  if (isRoleDescription(s) || echoesTitle(s)) return 'partial';
  return s.name.trim().split(/\s+/).filter(Boolean).length >= 2
    ? 'complete'
    : 'partial';
}

/**
 * Whether a later pass's answer should replace an earlier one.
 *
 * It must be stronger — a full name where we had a lone token — and it must
 * not contradict what we already had. The lower third on screen while someone
 * speaks can belong to a different person (b-roll, the interviewer), so a more
 * complete name is not automatically a better one. Consistency is judged by
 * the same scorer used everywhere else, at the review threshold: the question
 * is "does this contradict?", not "is this confidently the same person".
 * A role description named no one, so there is nothing to contradict.
 */
export function isRefinement(
  existing: IdentifiedSpeaker,
  incoming: IdentifiedSpeaker,
): boolean {
  if (identificationStrength(existing) === 'complete') return false;
  if (identificationStrength(incoming) !== 'complete') return false;
  if (isRoleDescription(existing)) return true;
  return (
    speakerMatchScore({ name: existing.name }, { name: incoming.name })
      .nameScore >= MATCH_THRESHOLD_REVIEW
  );
}

/**
 * Add new identifications and upgrade weak ones, in place.
 * Returns how many labels changed, so a caller can tell whether a pass helped.
 */
export function mergeIdentified(
  into: IdentifiedSpeaker[],
  incoming: IdentifiedSpeaker[],
): number {
  let changed = 0;
  incoming.forEach(candidate => {
    const index = into.findIndex(
      id => id.speakerLabel === candidate.speakerLabel,
    );
    if (index === -1) {
      into.push(candidate);
      changed++;
    } else if (isRefinement(into[index], candidate)) {
      into[index] = candidate;
      changed++;
    }
  });
  return changed;
}

/**
 * Did frame analysis actually read a lower third for this speaker?
 *
 * False both when the sweep looked and saw none, and when it never requested a
 * frame for them at all — turns shorter than the sampler's offset produce no
 * request. Those are the same thing to a caller deciding whether to look again.
 */
export function sawLowerThird(
  results: FrameAnalysisResult[],
  speakerLabel: string,
): boolean {
  return results.some(r => r.speakerLabel === speakerLabel && r.hasLowerThird);
}

/**
 * Should this speaker go to another, denser round of frames?
 *
 * Yes while they are unnamed or only partly named AND no lower third has been
 * read for them yet. No once an overlay has been read, whatever it said — the
 * denser passes resample the same turn rather than covering more of the video,
 * so a second look at ground already seen will not name them either.
 */
export function needsMoreFrames(
  identified: IdentifiedSpeaker[],
  frameResults: FrameAnalysisResult[],
  speakerLabel: string,
): boolean {
  const row = identified.find(id => id.speakerLabel === speakerLabel);
  if (!row) return true;
  if (identificationStrength(row) === 'complete') return false;
  return !sawLowerThird(frameResults, speakerLabel);
}
